import React, { useState, useEffect } from 'react';
import * as api from '../../../api/contentStudioV2';
import SpiderRadar from '../components/SpiderRadar';
import { useProgressWrapper } from '../../../utils/withProgress';

const Step6ReviewPrime = ({ sessionId, token, sessionData, onComplete, onBack, setError, setLoading }) => {
  const [narrative, setNarrative] = useState('');
  const [riskNotes, setRiskNotes] = useState([]);
  const [isPriming, setIsPriming] = useState(false);
  const [locale] = useState('en');
  
  // Creative Intent state
  const [showCreativeIntent, setShowCreativeIntent] = useState(false);
  const [style, setStyle] = useState('Dynamic');
  const [emotions, setEmotions] = useState(['Curiosity']);
  const [visualMode, setVisualMode] = useState('FaceToCamera');
  const [preferredFormat, setPreferredFormat] = useState('');
  const [notes, setNotes] = useState('');
  const [formatOptions, setFormatOptions] = useState([]);
  
  // NEW: Benefit Emphasis state
  const [benefitEmphasis, setBenefitEmphasis] = useState([]);
  const [showBenefitEmphasis, setShowBenefitEmphasis] = useState(true);  // Default to expanded for better UX
  const [appliedBenefitEmphasis, setAppliedBenefitEmphasis] = useState(null);  // Store applied emphasis for summary
  
  // Progress wrapper
  const withProgress = useProgressWrapper();

  // Check if SOA Plan already exists (user navigated back)
  useEffect(() => {
    if (sessionData?.soaPlan) {
      console.log('[Step6] Restoring existing SOA Plan from sessionData');
      setNarrative(sessionData.narrative || sessionData.soaPlan.narrative || '');
      setRiskNotes(sessionData.riskNotes || sessionData.soaPlan.risk_notes || []);
      // NOTE: Don't set isPriming to true here - priming is COMPLETE, not in progress
      // User should be able to click Re-Prime button again
      setIsPriming(false);
      
      // Restore applied benefit emphasis if exists
      if (sessionData.appliedBenefitEmphasis) {
        console.log('[Step6] Restoring applied benefit emphasis from sessionData');
        setAppliedBenefitEmphasis(sessionData.appliedBenefitEmphasis);
        // Also restore the current slider values to match what was applied
        setBenefitEmphasis(sessionData.appliedBenefitEmphasis);
      }
    }
    
    // Restore creative intent if exists
    if (sessionData?.creative_intent) {
      setStyle(sessionData.creative_intent.style || 'Dynamic');
      setEmotions(sessionData.creative_intent.emotions || ['Curiosity']);
      setVisualMode(sessionData.creative_intent.visual_mode || 'FaceToCamera');
      setPreferredFormat(sessionData.creative_intent.preferred_format || '');
      setNotes(sessionData.creative_intent.notes || '');
      setShowCreativeIntent(false); // Already saved
    }
  }, [sessionData]);
  
  // Load Format Intelligence Pack
  useEffect(() => {
    const loadFormats = async () => {
      try {
        const formatPack = await api.getFormatIntelligencePack(token);
        setFormatOptions(formatPack.formats || []);
      } catch (err) {
        console.error('Failed to load Format Intelligence Pack:', err);
      }
    };
    
    if (token) {
      loadFormats();
    }
  }, [token]);

  // Extract data from session
  const objective = sessionData?.objective || 'Sales';
  const audience = sessionData?.audience || {};
  const weights = sessionData?.weights || {};
  const evidenceMode = sessionData?.evidenceMode || 'Hybrid';
  const selectedProducts = sessionData?.selectedProducts || [];
  const selectedServices = sessionData?.selectedServices || [];
  const selectedValues = sessionData?.selectedValues || [];
  const selectedDirection = sessionData?.selectedDirection || [];
  const knowledgeSpider = sessionData?.knowledgeSpider || null;

  // NEW: Extract and initialize benefit emphasis from product packs
  useEffect(() => {
    const extractBenefits = () => {
      const benefits = [];
      
      console.log('\n' + '='.repeat(80));
      console.log('[STEP6 BENEFIT EXTRACT] 🎯 STARTING BENEFIT EXTRACTION');
      console.log('='.repeat(80));
      console.log('[STEP6] knowledgeSpider exists:', !!knowledgeSpider);
      console.log('[STEP6] selectedProducts count:', selectedProducts?.length || 0);
      
      // PRIORITY 1: Extract from knowledgeSpider (most reliable source)
      if (knowledgeSpider && knowledgeSpider.products && knowledgeSpider.products.length > 0) {
        console.log('[STEP6] ✅ Using knowledgeSpider.products:', knowledgeSpider.products.length);
        
        knowledgeSpider.products.forEach((product, prodIdx) => {
          // Try multiple possible benefit locations (V3 vs V4 structure)
          const productBenefits = 
            product?.content?.benefits?.product_benefits ||  // V4 structure
            product?.intelligence?.product_benefits ||        // Alternative V4 structure
            product?.content?.product_benefits ||             // V3 structure
            [];
          
          console.log(`\n[STEP6] Product ${prodIdx}:`, {
            pack_id: product?.pack_id,
            title: product?.metadata?.title?.en || product?.metadata?.title,
            v4_benefits: product?.content?.benefits?.product_benefits?.length || 0,
            v4_alt: product?.intelligence?.product_benefits?.length || 0,
            v3: product?.content?.product_benefits?.length || 0,
            using: productBenefits.length
          });
          
          productBenefits.forEach((benefit, benIdx) => {
            // Handle different benefit text structures
            const benefitText = 
              benefit?.benefit?.en ||           // Bilingual structure
              benefit?.benefit?.bm ||
              benefit?.title?.en ||             // Title structure
              benefit?.title?.bm ||
              benefit?.benefit ||               // Simple string
              benefit?.title ||
              'Unknown benefit';
            
            const benefitId = `product_${prodIdx}_benefit_${benIdx}`;
            benefits.push({
              benefit_id: benefitId,
              benefit_text: benefitText,
              emphasis: 50  // Default medium emphasis
            });
            
            console.log(`  [${benIdx}] ${benefitId}: "${benefitText.substring(0, 70)}..."`);
          });
        });
      }
      // FALLBACK: Try selectedProducts if knowledgeSpider not available
      else if (selectedProducts && selectedProducts.length > 0) {
        console.log('[Step6 Benefit Extract] Fallback to selectedProducts');
        
        selectedProducts.forEach((product, prodIdx) => {
          const productBenefits = 
            product?.content?.benefits?.product_benefits ||
            product?.intelligence?.product_benefits ||
            product?.content?.product_benefits ||
            [];
          
          console.log(`[Step6 Benefit Extract] SelectedProduct ${prodIdx}:`, productBenefits.length, 'benefits');
          
          productBenefits.forEach((benefit, benIdx) => {
            const benefitText = 
              benefit?.benefit?.en ||
              benefit?.benefit?.bm ||
              benefit?.title?.en ||
              benefit?.title?.bm ||
              benefit?.benefit ||
              benefit?.title ||
              'Unknown benefit';
            
            benefits.push({
              benefit_id: `product_${prodIdx}_benefit_${benIdx}`,
              benefit_text: benefitText,
              emphasis: 50
            });
          });
        });
      }
      
      console.log('\n[STEP6] ✅ EXTRACTION COMPLETE');
      console.log('[STEP6] Total benefits extracted:', benefits.length);
      if (benefits.length > 0) {
        console.log('[STEP6] 📋 ALL EXTRACTED BENEFITS:');
        benefits.forEach((b, i) => {
          console.log(`  [${i}] ${b.benefit_id}: "${b.benefit_text.substring(0, 70)}..."`);
        });
      } else {
        console.warn('[STEP6] ⚠️ WARNING: No benefits extracted! Sliders will not appear.');
      }
      console.log('='.repeat(80) + '\n');
      
      setBenefitEmphasis(benefits);
    };
    
    if ((knowledgeSpider && knowledgeSpider.products) || (selectedProducts && selectedProducts.length > 0)) {
      extractBenefits();
    }
  }, [selectedProducts, knowledgeSpider]);

  const selectedAmbassador = sessionData?.selectedAmbassador || [];

  const handlePrimeSOA = async () => {
    setIsPriming(true);
    setError(null);

    try {
      // Save current benefit emphasis for summary display
      if (benefitEmphasis.length > 0) {
        setAppliedBenefitEmphasis([...benefitEmphasis]);
      }
      
      const result = await withProgress(
        {
          label: 'Priming SOA questions',
          phases: [
            'Analyzing context',
            'Building narrative', 
            'Generating questions',
            'Localizing content'
          ],
          canCancel: false,
          timeoutMs: 30000,
          meta: { 
            session_id: sessionId,
            feature: 'content_studio_step6_prime'
          }
        },
        async (signal) => {
          // NEW: Include benefit emphasis if provided
          const emphasisToSend = benefitEmphasis.length > 0 ? benefitEmphasis : null;
          
          console.log('='.repeat(80));
          console.log('🔥 [STEP6 FRONTEND] SENDING PRIME REQUEST WITH BENEFIT EMPHASIS');
          console.log('='.repeat(80));
          console.log('[STEP6] sessionId:', sessionId);
          console.log('[STEP6] benefitEmphasis count:', benefitEmphasis.length);
          if (emphasisToSend) {
            console.log('[STEP6] 📤 BENEFIT EMPHASIS DATA BEING SENT:');
            emphasisToSend.forEach((emph, i) => {
              console.log(`  [${i}] benefit_id: ${emph.benefit_id} | emphasis: ${emph.emphasis} | text: ${emph.benefit_text?.substring(0, 60)}...`);
            });
          } else {
            console.log('[STEP6] ❌ No benefit emphasis being sent');
          }
          console.log('='.repeat(80));
          
          return await api.primeSOA(token, sessionId, emphasisToSend);
        }
      );
      
      console.log('[Step 6] Prime SOA result:', result);
      
      setNarrative(result.narrative);
      setRiskNotes(result.risk_notes || []);
      
      // ✅ Update sessionData with complete SOA plan including blueprint
      if (sessionData) {
        sessionData.soaPlan = {
          narrative: result.narrative,
          risk_notes: result.risk_notes || [],
          blueprint: result.blueprint || [],
          routing: result.routing || {}
        };
        // Save applied benefit emphasis to sessionData for restoration
        if (benefitEmphasis.length > 0) {
          sessionData.appliedBenefitEmphasis = appliedBenefitEmphasis;
        }
        console.log('[Step 6] Updated sessionData.soaPlan with blueprint:', sessionData.soaPlan.blueprint.length, 'questions');
      }
      
      // Show Creative Intent form after priming
      setShowCreativeIntent(true);
      setIsPriming(false);
    } catch (err) {
      if (err.message.includes('401') || err.message.includes('authentication')) {
        setError('Session expired — please login again');
      } else {
        setError('Failed to prime SOA questions: ' + err.message);
      }
      setIsPriming(false);
    }
  };
  
  const handleSaveCreativeIntent = async () => {
    setError(null);
    
    try {
      await withProgress(
        {
          label: 'Saving creative intent',
          phases: ['Validating', 'Saving preferences'],
          canCancel: false,
          timeoutMs: 10000,
          meta: { session_id: sessionId }
        },
        async (signal) => {
          return await api.saveCreativeIntent(token, sessionId, {
            style,
            emotions,
            visual_mode: visualMode,
            preferred_format: preferredFormat,
            notes
          });
        }
      );
      
      console.log('[Step 6] Saving Creative Intent and completing step');
      console.log('[Step 6] Blueprint length:', sessionData?.soaPlan?.blueprint?.length || 0);
      
      // Ensure soaPlan includes blueprint from priming
      const completeSoaPlan = sessionData?.soaPlan || { 
        narrative, 
        risk_notes: riskNotes,
        blueprint: [],
        routing: {}
      };
      
      // Validation: Check if blueprint exists
      if (!completeSoaPlan.blueprint || completeSoaPlan.blueprint.length === 0) {
        console.warn('[Step 6] WARNING: Blueprint is empty! Step 7 will fail.');
        setError('⚠️ SOA questions not generated. Please click "Prime SOA Questions" first.');
        return;
      }
      
      // Pass both SOA plan (with blueprint) and creative intent to next step
      onComplete({ 
        soaPlan: completeSoaPlan,
        narrative,
        riskNotes,
        creative_intent: {
          style,
          emotions,
          visual_mode: visualMode,
          preferred_format: preferredFormat,
          notes
        }
      });
    } catch (err) {
      setError('Failed to save creative intent: ' + err.message);
    }
  };
  
  const toggleEmotion = (emotion) => {
    if (emotions.includes(emotion)) {
      setEmotions(emotions.filter(e => e !== emotion));
    } else {
      setEmotions([...emotions, emotion]);
    }
  };

  // Render chip helper
  const renderChip = (text, bg = '#E8F4FF', color = '#0e67a2') => (
    <span style={{
      padding: '4px 10px',
      background: bg,
      color: color,
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 600,
      whiteSpace: 'nowrap'
    }}>
      {text}
    </span>
  );

  return (
    <div style={{ background: '#fff', borderRadius: '12px', padding: '12px' }}>
      <h2 style={{ fontSize: '18px', color: '#333', marginBottom: '8px' }}>
        Review & Prime
      </h2>
      <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
        Review your inputs before generating personalized SOA questions
      </p>

      {/* Card 0: Platform & Duration (Read-Only Display) */}
      <div style={{
        padding: '10px',
        background: '#FAFAFA',
        borderRadius: '8px',
        marginBottom: '10px',
        border: '1px solid #E0E0E0'
      }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '6px' }}>
          📱 Platform & Duration
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
          {(sessionData?.platformContext?.channels || []).map((platform, i) => (
            <span key={`platform-${platform}`}>{renderChip(platform, '#E8F4FF', '#0e67a2')}</span>
          ))}
          {sessionData?.platformContext?.normalizedSeconds && renderChip(
            `${sessionData.platformContext.normalizedSeconds}s`, 
            '#FFF4E6', 
            '#F7941E'
          )}
        </div>
      </div>

      {/* Card 1: Objective */}
      <div style={{
        padding: '10px',
        background: '#FAFAFA',
        borderRadius: '8px',
        marginBottom: '10px',
        border: '1px solid #E0E0E0'
      }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '6px' }}>
          Objective
        </div>
        <div>
          {renderChip(objective, objective === 'Sales' ? '#E8F5E9' : '#FFF4E6', objective === 'Sales' ? '#2E7D32' : '#F7941E')}
        </div>
      </div>

      {/* Card 2: Audience */}
      <div style={{
        padding: '10px',
        background: '#FAFAFA',
        borderRadius: '8px',
        marginBottom: '10px',
        border: '1px solid #E0E0E0'
      }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '6px' }}>
          Audience
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
          {audience.countries?.map((c, i) => <span key={`country-${c}`}>{renderChip(`🇲🇾 ${c}`)}</span>)}
          {audience.age_ranges?.slice(0, 3).map((a, i) => <span key={`age-${a}`}>{renderChip(a)}</span>)}
          {audience.age_ranges?.length > 3 && renderChip(`+${audience.age_ranges.length - 3} more`)}
          {renderChip(audience.gender || 'All')}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
          {audience.location_types?.map((l, i) => <span key={`loc-${l}`}>{renderChip(l, '#FFF4E6', '#F7941E')}</span>)}
        </div>
        {/* Interests Row */}
        {audience.interests && audience.interests.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
            {audience.interests.map((interest, i) => (
              <span key={`interest-${interest}`}>{renderChip(`💡 ${interest}`, '#E8F5E9', '#2E7D32')}</span>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {audience.languages?.map((l, i) => <span key={`lang-${l}`}>{renderChip(l, '#E8F4FF', '#0e67a2')}</span>)}
          {audience.slang?.slice(0, 2).map((s, i) => <span key={`slang-${s}`}>{renderChip(s, '#FFEBEE', '#C62828')}</span>)}
          {renderChip(audience.tone_preference || 'Mix', '#F3E5F5', '#7B1FA2')}
        </div>
      </div>

      {/* Card 3: Knowledge Packs */}
      <div style={{
        padding: '10px',
        background: '#FAFAFA',
        borderRadius: '8px',
        marginBottom: '10px',
        border: '1px solid #E0E0E0'
      }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '6px' }}>
          Knowledge Packs
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {selectedProducts.length > 0 && renderChip(`${selectedProducts.length} Product${selectedProducts.length > 1 ? 's' : ''}`, '#E8F5E9', '#2E7D32')}
          {selectedServices.length > 0 && renderChip(`${selectedServices.length} Service${selectedServices.length > 1 ? 's' : ''}`, '#E8F5E9', '#2E7D32')}
          {selectedValues.length > 0 && renderChip(`${selectedValues.length} Values`, '#E3F2FD', '#0e67a2')}
          {selectedDirection.length > 0 && renderChip(`${selectedDirection.length} Direction`, '#E3F2FD', '#0e67a2')}
          {selectedAmbassador.length > 0 && renderChip(`${selectedAmbassador.length} Ambassador`, '#E3F2FD', '#0e67a2')}
        </div>
      </div>

      {/* Card 4: Spider Web Snapshot */}
      <div style={{
        padding: '10px',
        background: '#FAFAFA',
        borderRadius: '8px',
        marginBottom: '10px',
        border: '1px solid #E0E0E0'
      }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '6px' }}>
          Spider Web Weights
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <SpiderRadar 
            values={weights} 
            size={180}
          />
        </div>
      </div>

      {/* Card 5: Evidence Mode */}
      <div style={{
        padding: '10px',
        background: '#FAFAFA',
        borderRadius: '8px',
        marginBottom: '10px',
        border: '1px solid #E0E0E0'
      }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '6px' }}>
          Evidence Mode
        </div>
        <div>
          {renderChip(evidenceMode, evidenceMode === 'Scientific' ? '#E3F2FD' : evidenceMode === 'Emotional' ? '#FCE4EC' : '#FFF4E6', evidenceMode === 'Scientific' ? '#0e67a2' : evidenceMode === 'Emotional' ? '#C2185B' : '#F57C00')}
        </div>
      </div>

      {/* Card 6: Auto Narrative (after priming) */}
      {narrative && (
        <div style={{
          padding: '10px',
          background: '#E8F5E9',
          borderRadius: '8px',
          marginBottom: '10px',
          border: '1px solid #4CAF50'
        }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#2E7D32', marginBottom: '6px' }}>
            💡 Context Narrative
          </div>
          <div style={{ fontSize: '13px', color: '#1B5E20', lineHeight: '1.5' }}>
            {narrative}
          </div>
        </div>
      )}

      {/* NEW: Benefit Emphasis Summary (after priming) */}
      {narrative && appliedBenefitEmphasis && appliedBenefitEmphasis.length > 0 && (
        <div style={{
          padding: '10px',
          background: '#FFF9E6',
          borderRadius: '8px',
          marginBottom: '10px',
          border: '1px solid #FFD54F'
        }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#F57F17', marginBottom: '6px' }}>
            🎯 Applied Benefit Emphasis
          </div>
          <div style={{ fontSize: '11px', color: '#666', marginBottom: '8px' }}>
            Pain point suggestions were focused based on these benefit weights:
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {appliedBenefitEmphasis
              .filter(b => b.emphasis >= 30)  // Only show benefits that were included (>= 30)
              .sort((a, b) => b.emphasis - a.emphasis)  // Sort by emphasis (high to low)
              .slice(0, 5)  // Show top 5
              .map((benefit, idx) => {
                const getColor = (val) => {
                  if (val >= 70) return { bg: '#FFEBEE', text: '#C62828' };
                  if (val >= 50) return { bg: '#FFF4E6', text: '#F57C00' };
                  return { bg: '#F5F5F5', text: '#666' };
                };
                const colors = getColor(benefit.emphasis);
                
                return (
                  <div key={`item-${idx}`} style={{
                    padding: '6px 10px',
                    background: colors.bg,
                    color: colors.text,
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span style={{ fontWeight: 700 }}>{benefit.emphasis}</span>
                    <span>{benefit.benefit_text.length > 40 ? benefit.benefit_text.substring(0, 40) + '...' : benefit.benefit_text}</span>
                  </div>
                );
              })}
          </div>
          {appliedBenefitEmphasis.filter(b => b.emphasis >= 30).length > 5 && (
            <div style={{ fontSize: '10px', color: '#999', marginTop: '6px', fontStyle: 'italic' }}>
              +{appliedBenefitEmphasis.filter(b => b.emphasis >= 30).length - 5} more benefits included
            </div>
          )}
        </div>
      )}

      {/* NEW: Card 7: Benefit Emphasis (Optional) - Always visible when benefits exist */}
      {benefitEmphasis.length > 0 && (
        <div style={{
          padding: '12px',
          background: '#FFF9E6',
          borderRadius: '8px',
          marginBottom: '10px',
          border: '1px solid #FFD54F'
        }}>
          {/* Header - Always visible (no toggle) */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#F57F17', marginBottom: '6px' }}>
              🎯 Benefit Emphasis {narrative ? '(Adjust & Re-prime)' : '(Optional)'}
            </div>
            <p style={{ fontSize: '11px', color: '#666', marginBottom: '12px', lineHeight: '1.4' }}>
              {narrative ? (
                <>
                  ✏️ You can adjust sliders and click <strong>"Prime SOA Questions"</strong> again to regenerate with new emphasis.
                </>
              ) : (
                <>
                  💡 Adjust sliders to emphasize which benefits should drive pain point suggestions. 
                  Higher values = more focused pain points for that benefit. 
                  Benefits below 30 will be excluded.
                </>
              )}
            </p>
          </div>
              
              {/* Quick Presets */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    setBenefitEmphasis(benefitEmphasis.map(b => ({ ...b, emphasis: 50 })));
                  }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '11px',
                    background: '#fff',
                    border: '1px solid #FFD54F',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: '#F57F17'
                  }}
                >
                  Reset All (50)
                </button>
                <button
                  onClick={() => {
                    setBenefitEmphasis(benefitEmphasis.map((b, idx) => ({
                      ...b,
                      emphasis: idx < 3 ? 80 : 20
                    })));
                  }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '11px',
                    background: '#fff',
                    border: '1px solid #FFD54F',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: '#F57F17'
                  }}
                >
                  Focus Top 3
                </button>
              </div>
              
              {/* Individual Benefit Sliders */}
              <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                {benefitEmphasis.map((benefit, idx) => {
                  const getEmphasisLabel = (val) => {
                    if (val >= 70) return { text: '🔥 High', color: '#e74c3c' };
                    if (val >= 40) return { text: '➡️ Medium', color: '#f39c12' };
                    if (val > 0) return { text: '⬇️ Low', color: '#95a5a6' };
                    return { text: '❌ Off', color: '#bdc3c7' };
                  };
                  
                  const label = getEmphasisLabel(benefit.emphasis);
                  
                  return (
                    <div 
                      key={benefit.benefit_id}
                      style={{
                        marginBottom: '10px',
                        padding: '8px',
                        background: '#fff',
                        borderRadius: '6px',
                        border: '1px solid #f0f0f0'
                      }}
                    >
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#333', 
                        marginBottom: '6px',
                        fontWeight: 500
                      }}>
                        {benefit.benefit_text}
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="10"
                          value={benefit.emphasis}
                          onChange={(e) => {
                            const newEmphasis = [...benefitEmphasis];
                            newEmphasis[idx].emphasis = parseInt(e.target.value);
                            setBenefitEmphasis(newEmphasis);
                          }}
                          style={{
                            flex: 1,
                            height: '4px',
                            borderRadius: '2px',
                            outline: 'none',
                            WebkitAppearance: 'none',
                            background: `linear-gradient(to right, #FFD54F ${benefit.emphasis}%, #e0e0e0 ${benefit.emphasis}%)`
                          }}
                        />
                        
                        <div style={{ 
                          minWidth: '35px', 
                          textAlign: 'center',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#333'
                        }}>
                          {benefit.emphasis}
                        </div>
                        
                        <div style={{
                          minWidth: '90px',
                          fontSize: '11px',
                          fontWeight: 600,
                          color: label.color
                        }}>
                          {label.text}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div style={{ 
                fontSize: '10px', 
                color: '#999', 
                marginTop: '8px',
                fontStyle: 'italic'
              }}>
                💡 Tip: Use "Focus Top 3" for most campaigns. Leave at 50 for balanced suggestions.
              </div>
        </div>
      )}

      {/* Card 8: Compliance & Risks (after priming) */}
      {riskNotes.length > 0 && (
        <div style={{
          padding: '10px',
          background: '#FFF4E6',
          borderRadius: '8px',
          marginBottom: '10px',
          border: '1px solid #F7941E'
        }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#F57C00', marginBottom: '6px' }}>
            ⚠️ Compliance & Risk Notes
          </div>
          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#E65100', lineHeight: '1.5' }}>
            {riskNotes.map((note, i) => (
              <li key={`note-${i}-${note?.slice(0,10)}`}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Creative Intent Section (Step 6 Enhancement) */}
      {showCreativeIntent && isPriming && (
        <div style={{
          padding: '16px',
          background: '#F3E5F5',
          borderRadius: '12px',
          marginBottom: '12px',
          border: '1px solid #CE93D8'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#7B1FA2', marginBottom: '12px' }}>
            ✨ Creator Intent (Optional)
          </h3>
          <p style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
            Guide the AI on your creative vision for this content.
          </p>

          {/* Style */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#333', display: 'block', marginBottom: '8px' }}>
              Creative Style
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['Dynamic', 'Calm', 'Formal', 'Playful'].map((s) => (
                <button
                  key={s}
                  onClick={() => setStyle(s)}
                  style={{
                    padding: '10px',
                    background: style === s ? '#7B1FA2' : '#fff',
                    color: style === s ? '#fff' : '#333',
                    border: '1px solid #CE93D8',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Emotions */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#333', display: 'block', marginBottom: '8px' }}>
              Target Emotions (select multiple)
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {['Curiosity', 'Empathy', 'Joy', 'Shock', 'Warmth'].map((emotion) => (
                <button
                  key={emotion}
                  onClick={() => toggleEmotion(emotion)}
                  style={{
                    padding: '8px 14px',
                    background: emotions.includes(emotion) ? '#7B1FA2' : '#fff',
                    color: emotions.includes(emotion) ? '#fff' : '#333',
                    border: '1px solid #CE93D8',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {emotions.includes(emotion) ? '✓ ' : ''}{emotion}
                </button>
              ))}
            </div>
          </div>

          {/* Visual Mode */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#333', display: 'block', marginBottom: '8px' }}>
              Visual Mode
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              {['FaceToCamera', 'VoiceOver', 'ProductDemo', 'Mixed'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setVisualMode(mode)}
                  style={{
                    padding: '10px',
                    background: visualMode === mode ? '#7B1FA2' : '#fff',
                    color: visualMode === mode ? '#fff' : '#333',
                    border: '1px solid #CE93D8',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>

          {/* Preferred Format */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#333', display: 'block', marginBottom: '8px' }}>
              Preferred Format (Optional)
            </label>
            <select
              value={preferredFormat}
              onChange={(e) => setPreferredFormat(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                background: '#fff',
                border: '1px solid #CE93D8',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#333'
              }}
            >
              <option value="">-- No Preference --</option>
              {formatOptions.map((format) => (
                <option key={format.id} value={format.id}>
                  {format.title?.en || format.id}
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#333', display: 'block', marginBottom: '8px' }}>
              Additional Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any specific ideas, tone, or creative direction you'd like to convey..."
              rows={3}
              style={{
                width: '100%',
                padding: '10px',
                background: '#fff',
                border: '1px solid #CE93D8',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#333',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Save Creative Intent Button */}
          <button
            onClick={handleSaveCreativeIntent}
            style={{
              width: '100%',
              padding: '12px',
              background: '#7B1FA2',
              border: 'none',
              borderRadius: '10px',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            ✅ Save & Continue to SOA Drill
          </button>

          <button
            onClick={() => setShowCreativeIntent(false)}
            style={{
              width: '100%',
              padding: '10px',
              background: 'transparent',
              border: 'none',
              color: '#666',
              fontSize: '13px',
              cursor: 'pointer',
              marginTop: '8px'
            }}
          >
            Skip Creative Intent (Proceed with Defaults)
          </button>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
        <button 
          onClick={onBack} 
          disabled={isPriming && !narrative}
          style={{ 
            flex: 1, 
            padding: '12px', 
            background: '#fff', 
            border: '2px solid #0e67a2', 
            borderRadius: '10px', 
            color: '#0e67a2', 
            fontSize: '14px', 
            fontWeight: 600, 
            cursor: (isPriming && !narrative) ? 'not-allowed' : 'pointer',
            opacity: (isPriming && !narrative) ? 0.5 : 1
          }}
        >
          Back
        </button>
        
        {/* Show both Re-Prime and Continue buttons if already primed, otherwise show Prime button */}
        {narrative ? (
          <>
            <button 
              onClick={handlePrimeSOA}
              disabled={isPriming}
              style={{ 
                flex: 1, 
                padding: '12px', 
                background: isPriming ? '#ccc' : '#F7941E', 
                border: 'none', 
                borderRadius: '10px', 
                color: '#fff', 
                fontSize: '14px', 
                fontWeight: 600, 
                cursor: isPriming ? 'not-allowed' : 'pointer',
                marginRight: '8px'
              }}
            >
              {isPriming ? 'Re-priming...' : '🔄 Re-Prime'}
            </button>
            <button 
              onClick={() => {
                // Ensure blueprint is included from sessionData
                const completeSoaPlan = sessionData?.soaPlan || {
                  narrative,
                  risk_notes: riskNotes,
                  blueprint: [],
                  routing: {}
                };
                
                console.log('[Step 6] Continue clicked, blueprint length:', completeSoaPlan.blueprint?.length || 0);
                
                // Validation: Warn if blueprint missing
                if (!completeSoaPlan.blueprint || completeSoaPlan.blueprint.length === 0) {
                  alert('⚠️ SOA Blueprint is missing! This will cause Step 7 to fail. Please refresh and click "Prime SOA Questions" again.');
                  return;
                }
                
                onComplete({ 
                  soaPlan: completeSoaPlan,
                  narrative,
                  riskNotes
                });
              }}
              style={{ 
                flex: 1, 
                padding: '12px', 
                background: '#4CAF50', 
                border: 'none', 
                borderRadius: '10px', 
                color: '#fff', 
                fontSize: '14px', 
                fontWeight: 600, 
                cursor: 'pointer' 
              }}
            >
              ✅ Continue to SOA Drill
            </button>
          </>
        ) : (
          <button 
            onClick={handlePrimeSOA}
            disabled={isPriming}
            style={{ 
              flex: 2, 
              padding: '12px', 
              background: isPriming ? '#ccc' : '#F7941E', 
              border: 'none', 
              borderRadius: '10px', 
              color: '#fff', 
              fontSize: '14px', 
              fontWeight: 600, 
              cursor: isPriming ? 'not-allowed' : 'pointer' 
            }}
          >
            {isPriming ? 'Priming SOA...' : '🎯 Prime SOA Questions'}
          </button>
        )}
      </div>
    </div>
  );
};

export default Step6ReviewPrime;
